import Bathtub from '@/icons/light/bathtub.svg';
import Bedroom from '@/icons/light/bedroom.svg';
import Garaje from '@/icons/light/garaje.svg';
import Mueble from '@/icons/light/mueble.svg';
import Otros from '@/icons/light/otros.svg';
import Piscina from '@/icons/light/piscina.svg';
import Sun from '@/icons/light/sun.svg';

import Generic from '@/assets/images/generic.jpg';
import { flatName, flatPath } from '@/components/algolia/utils';
import { generatedListingCardTheme } from '@/lib/contexts/listing-card-theme.generated';
import { formatNumberToCurrency } from '@/utils';

import type { TypeFlatAnalytics } from '@/types';

// Local-only escape hatch: cuando las URLs de fotos del catálogo (SAP/CDN
// del Strapi remoto) no son alcanzables desde el dev local, encender
// `NEXT_PUBLIC_FORCE_PHOTO_FALLBACK=true` en `.env` fuerza siempre el
// asset bundled para no ver iconos de imagen rota. No tocar en prod.
const FORCE_FALLBACK = process.env.NEXT_PUBLIC_FORCE_PHOTO_FALLBACK === 'true';

type DescOptions = {
  surface: boolean;
  extras: boolean;
};

export const cardTransformer = (
  hit: any,
  onClick: () => void,
  // Por defecto leemos el tema generado en build-time desde
  // ThemeConfiguration — así los 4 callers (GridCardHits,
  // ViviendasMasBuscadas, PropertyMap, InnerMap popup) heredan los
  // flags del tenant sin tener que threadear nada.
  descOptions: DescOptions = generatedListingCardTheme.desc,
) => {
  const isForSale = hit?.ventaAlquiler?.toLowerCase() === 'venta';

  // MED_EST mantiene la copia histórica ("Consultar disponibilidad")
  // independientemente de las toggles del tenant — son alquileres por
  // temporada y no aplican los flags de m²/extras del catálogo normal.
  const description =
    hit?.ventaAlquiler === 'MED_EST'
      ? ['Consultar disponibilidad']
      : [
          ...(descOptions.surface && hit?.metrosComputablesUA
            ? [`${hit.metrosComputablesUA} m²`]
            : []),
          ...(descOptions.extras &&
          hit?.hasGaraje !== 'No' &&
          hit?.hasTraseros !== 'No'
            ? ['Garaje y Trastero incluidos']
            : []),
        ];

  return {
    image: {
      alt: `${hit?.tipoDeUsoDescription} en ${hit?.ciudad}`,
      src: (!FORCE_FALLBACK && hit?.fotos?.[0]?.url) || Generic.src,
    },
    title: flatName(hit),
    price: formatNumberToCurrency(
      isForSale
        ? (hit?.importeVenta as number)
        : (hit?.importeRecibofinal as number),
    ),
    period: isForSale ? '' : '/mes',
    // El badge sólo se muestra cuando el editor ha marcado `nuevo` en
    // la entry de zfm-disponiblidad. El estilo (pill / markhighlight) y
    // el texto los controla el tenant via ThemeConfiguration; aquí sólo
    // decidimos visibilidad. `tag: undefined` → CardFlat no renderiza el
    // `label` del Card.
    tag: hit?.nuevo ? '¡Nuevo!' : undefined,
    description,
    features: [
      ...(hit?.habitaciones
        ? [{ Icon: Bedroom, value: `${hit.habitaciones} Habs.` }]
        : []),
      ...(hit?.banos ? [{ Icon: Bathtub, value: `${hit.banos} baños` }] : []),
      ...(hit?.hasAmueblado !== 'No'
        ? [
            {
              Icon: Mueble,
              value: 'Amueb.',
            },
          ]
        : []),
      ...(hit?.hasPiscina !== 'No'
        ? [
            {
              Icon: Piscina,
              value: 'Piscina',
            },
          ]
        : []),
      ...(hit?.hasGaraje !== 'No'
        ? [
            {
              Icon: Garaje,
              value: 'Garaje',
            },
          ]
        : []),
      ...(hit?.hasTerrazas !== 'No'
        ? [
            {
              Icon: Sun,
              value: 'Terraza',
            },
          ]
        : []),
      ...(hit?.hasTraseros !== 'No'
        ? [
            {
              Icon: Otros,
              value: 'Trastero',
            },
          ]
        : []),
    ],
    button: {
      label: 'Contactar',
      onClick: onClick,
    },
    href: flatPath(hit),
    flat: {
      codigoUnidadAlquiler: hit?.codigoUnidadAlquiler as string,
      reservable: hit?.reservable as string,
      visitable: hit?.visitable as string,
      nombreUE: hit?.nombreUE as string,
      ventaAlquiler: hit?.ventaAlquiler as string,
      name: flatName(hit),
      maxOcupantes: hit?.maxOcupantes as string,
    } as TypeFlatAnalytics,
  };
};
